// Patient workspace — the OVERLAY surface (Blueprint §2 screen 5, NOT a nav
// route). Ports the semantics of legacy openPatientWorkspace / renderWSHead /
// renderTimeline / renderSidePanel / callPatient / openCallLog / openAEForm /
// openMsgForm (v20 lines ~2528-2719, 2744-3010, 3013-3291, 3891-3931) onto the
// 2.0 data layer (data/workspace.js, data/ae.js, data/assign.js) + i18n messages.
//
// Structure (top → bottom): header (avatar, name, programme badge, ref,
// click-to-call phone, facility/county, enrolled-ago, High-risk/Archived tags,
// quick-action row) → stages strip (resolveStage) → next action → activity
// timeline (with ownership history merged) · side panel (details incl. "· N
// moves", prescription, quick actions).
//
// FAILURE TAXONOMY (R-003): the case read failing shows an in-place header retry;
// each of the eight panels loads independently and a failed panel shows its OWN
// inline retry — never a red whole-screen. The ownership-history (audit) panel
// degrades SILENTLY (its failure never reddens the timeline).
//
// LATEST-WINS: a flight() epoch guards every open — click patient B while A is
// still loading and A's data never lands under B's header (legacy wsEpoch).

import { store, SLICES, flight } from '../state/store.js';
import { esc } from '../ui/esc.js';
import { toast } from '../ui/toast.js';
import { register } from '../ui/actions.js';
import { openModal, closeModal, setBtnBusy, trapStep } from '../ui/modal.js';
import { netErr } from '../errors.js';
import { canAssign } from '../auth.js';
import {
  isClosedCase,
  resolveStage,
  STAGES,
  SUSPECT_DEFAULT,
  AE_SEVERITY,
  AE_OUTCOME,
  AE_SERIOUSNESS_CRITERIA,
  AE_EMAIL,
  CANCER_STAGES,
  DELIVERY_METHODS,
} from '../db/contract.js';
import { initials } from './shell.js';
import { progDotHTML } from '../programmes.js';
import { dueLabel } from './today.js';
import { userName, shortName } from '../data/users.js';
import { loadWorkspace, logContact, updateClinicalDetails } from '../data/workspace.js';
import { validateAE, reportAdverseEvent, markAENotified, buildAEEmailBody, aeEmailSubject, buildMailto } from '../data/ae.js';
import { openAssignModal, pickUp } from './assign-modal.js';
import { openArchiveModal } from './archive-modal.js';
import {
  MESSAGE_TEMPLATES,
  renderTemplate,
  templateLabel,
  pickLanguage,
  readLastLang,
  writeLastLang,
  draftIsDirty,
  LANG_LABEL,
} from '../i18n/messages.js';

// ─── Pure helpers (unit-tested without a DOM) ──────────────────────────────

/**
 * "· N moves" suffix for the Owner field (legacy renderSidePanel), '' at zero.
 * @param {number} n
 * @returns {string}
 */
export function movesLabel(n) {
  const m = Number(n) || 0;
  if (m <= 0) return '';
  return `· ${m} move${m === 1 ? '' : 's'}`;
}

/**
 * Is a phone number actually dialable? (legacy callPatient guard: not empty,
 * not the literal 'undefined'/'null', not a masked +254 XXX placeholder.)
 * @param {any} phone
 * @returns {boolean}
 */
export function phoneCallable(phone) {
  const p = String(phone === null || phone === undefined ? '' : phone).trim();
  if (!p || p === 'undefined' || p === 'null') return false;
  return !/x{3,}/i.test(p);
}

/**
 * One ownership-history timeline event from an audit_events case_assigned row
 * (the round-table feature). Self-pickup reads "Picked up by …"; a move reads
 * "→ New owner · by Actor"; the reassign note becomes the muted desc line. Names
 * are resolved via the passed short() so it tests without a store.
 * @param {any} row audit_events row (details{to_user, by_id, self_pickup, notes})
 * @param {(id: any) => string} short id → "First L."
 * @returns {{date: any, title: string, desc: string, cls: string}}
 */
export function ownershipEvent(row, short) {
  const d = (row && row.details) || {};
  const actor = (row && row.actor_user_id) || d.by_id;
  const title = d.self_pickup
    ? `👤 Picked up by ${short(actor)}`
    : `👤 → ${short(d.to_user)} · by ${short(actor)}`;
  return { date: row && row.occurred_at, title, desc: d.notes || '', cls: '' };
}

/**
 * Build the merged, sorted activity timeline (legacy renderTimeline): open tasks
 * first (soonest due), then all history newest-first. Ownership moves are folded
 * in as first-class events. Pure — name resolvers passed in.
 * @param {{tasks?: any[], contacts?: any[], aes?: any[], deliveries?: any[], stageHist?: any[], assignHist?: any[]}} src
 * @param {Date} now
 * @param {{name: (id: any) => string, short: (id: any) => string}} res
 * @returns {Array<{date: any, title: string, desc: string, cls: string, open?: boolean, taskId?: any, taskTitle?: any}>}
 */
export function buildTimeline(src, now, res) {
  const s = src || {};
  const name = res.name;
  const short = res.short;
  const evts = [];
  (s.tasks || []).filter((t) => ['open', 'in_progress', 'snoozed'].includes(t.status)).forEach((t) => {
    const overdue = t.due_at && new Date(t.due_at) < now;
    evts.push({
      date: t.due_at || t.created_at,
      title: `Task: ${t.title || (t.task_type || '').replace(/_/g, ' ')} — ${t.status}`,
      desc: t.due_at ? `Due ${dueLabel(t.due_at, now)}` : '',
      cls: overdue ? 'dn' : 'wn',
      open: true,
      taskId: t.task_id,
      taskTitle: t.title || t.task_type,
    });
  });
  (s.tasks || []).filter((t) => t.status === 'completed').forEach((t) => {
    evts.push({
      date: t.closed_at || t.created_at,
      title: `Task: ${t.title || (t.task_type || '').replace(/_/g, ' ')}`,
      desc: (t.closed_reason || 'Completed') + (t.closed_by ? ` · by ${name(t.closed_by)}` : ''),
      cls: 'ok',
    });
  });
  (s.contacts || []).forEach((c) => {
    const reached = (c.outcome || '').includes('reached');
    const chan = c.channel || c.kind;
    evts.push({
      date: c.contact_at || c.occurred_at || c.created_at,
      title: `Contact: ${(c.outcome || 'logged').replace(/_/g, ' ')}`,
      desc: (c.notes || c.note || '') + (chan ? ` · via ${chan}` : '') + (c.created_by ? ` · by ${name(c.created_by)}` : ''),
      cls: reached ? 'ok' : '',
    });
  });
  (s.aes || []).forEach((a) => {
    evts.push({
      date: a.created_at || a.reported_at,
      title: `AE: ${a.reaction_term || a.description || 'reaction'}`,
      desc: `${a.severity || ''} ${a.ae_description || a.description || ''}`.trim(),
      cls: 'dn',
    });
  });
  (s.deliveries || []).forEach((d) => {
    const st = (d.status || '').toLowerCase();
    const done = st === 'received' || st === 'treatment_started';
    evts.push({
      date: d.dispatch_date || d.created_at,
      title: `Delivery: ${d.status || 'planned'}`,
      desc: (d.notes || '') + (d.created_by ? ` · by ${name(d.created_by)}` : ''),
      cls: done ? 'ok' : '',
    });
  });
  (s.assignHist || []).forEach((a) => evts.push(ownershipEvent(a, short)));
  (s.stageHist || []).forEach((st) => {
    const from = (st.from_stage || '').replace(/_/g, ' ');
    const to = (st.to_stage || '').replace(/_/g, ' ');
    evts.push({
      date: st.created_at,
      title: `Stage: ${from} → ${to}`,
      desc: (st.reason || '') + (st.changed_by ? ` · by ${name(st.changed_by)}` : ''),
      cls: '',
    });
  });
  const open = evts.filter((e) => e.open).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const hist = evts.filter((e) => !e.open).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  return [...open, ...hist];
}

/**
 * Which ownership button(s) the header shows (legacy renderWSHead actsHTML): a
 * closed case shows none; an unowned case shows Pick up + Assign (plain staff) or
 * just Assign (coordinators/admins route rather than self-hold); an owned case
 * shows Reassign. Pure.
 * @param {any} c the case
 * @param {boolean} canAssignRole
 * @returns {Array<'pickup'|'assign'|'reassign'>}
 */
export function workspaceOwnershipButtons(c, canAssignRole) {
  if (!c || isClosedCase(c)) return [];
  if (!c.assigned_to) return canAssignRole ? ['assign'] : ['pickup', 'assign'];
  return ['reassign'];
}

/**
 * Compact "time ago / until" for timeline stamps (legacy tAgo, condensed).
 * @param {any} d ISO
 * @param {Date} [now]
 * @returns {string}
 */
export function tAgo(d, now) {
  if (!d) return '';
  const base = now ? now.getTime() : Date.now();
  const diff = (base - new Date(d).getTime()) / 1000;
  const abs = Math.abs(diff);
  const fwd = diff < 0 ? 'in ' : '';
  const suf = diff < 0 ? '' : ' ago';
  if (abs < 60) return 'just now';
  if (abs < 3600) return fwd + Math.floor(abs / 60) + 'm' + suf;
  if (abs < 86400) return fwd + Math.floor(abs / 3600) + 'h' + suf;
  if (abs < 86400 * 30) return fwd + Math.floor(abs / 86400) + 'd' + suf;
  return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Communication-channel preference (legacy commsPref): 'yes'|'no'|'unknown'. */
export function commsPref(caseRow, channel) {
  const cc = (caseRow && caseRow.comms_consent) || {};
  if (typeof cc[channel] !== 'boolean') return 'unknown';
  return cc[channel] ? 'yes' : 'no';
}

// ─── Icons (inline SVG; no external assets, CSP-safe) ──────────────────────

/** @param {string} paths @param {number} [size] @returns {string} */
function ic(paths, size = 16) {
  return (
    `<svg class="ic" viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" ` +
    'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    paths +
    '</svg>'
  );
}
const IC_BACK = '<polyline points="15 18 9 12 15 6"/>';
const IC_PHONE = '<path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.6A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z"/>';
const IC_CHAT = '<path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.4 8.4 0 0 1-3.8-.8L3 21l1.9-5.1A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z"/>';
const IC_NOTE = '<path d="M11 4H4v16h16v-7"/><path d="M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z"/>';
const IC_WARN = '<path d="M12 4L2.5 20h19z"/><path d="M12 10v4m0 3v.5"/>';
const IC_DOTS = '<circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/>';
const IC_STAGE = '<polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/>';

/** Deterministic avatar colour (port of legacy ac()). */
function avatarColor(name) {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return `hsl(${h} 42% 42%)`;
}

// ─── Module state ──────────────────────────────────────────────────────────

const _ws = flight(); // latest-wins epoch for opens
let _openId = /** @type {string|null} */ (null); // the case currently displayed
let _prevFocus = /** @type {Element|null} */ (null);
let _wired = false;
let _pickLock = false;
// Composer draft state (bilingual message composer).
let _compLang = /** @type {'en'|'sw'} */ ('en');
let _compTpl = /** @type {string|null} */ (null);
let _compLastGen = ''; // the text the composer last auto-filled (dirty check)
let _compPhone = '';

/** Test-only: reset module state. @returns {void} */
export function _resetWorkspace() {
  _openId = null;
  _prevFocus = null;
  _wired = false;
  _pickLock = false;
  _compLang = 'en';
  _compTpl = null;
  _compLastGen = '';
  _compPhone = '';
}

function doc() {
  return /** @type {any} */ (globalThis).document;
}
function session() {
  return store.get(SLICES.SESSION) || {};
}
function selfId() {
  const s = session();
  return s.user && s.user.id;
}
function role() {
  return session().role;
}
/** Programme catalogue row for a code (display name, drug, PV inbox). */
function progFor(code) {
  const prog = store.get(SLICES.PROGRAMMES) || {};
  return (prog.byCode && prog.byCode[code]) || null;
}
function programmeHex(code) {
  const p = progFor(code);
  return p && p.brand_accent_hex;
}

// ─── Overlay host ──────────────────────────────────────────────────────────

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Tab focus trap for the overlay (aria-modal="true" must be backed by real focus
 * containment). Bails when a modal sits on top — the modal owns its own trap.
 * @param {any} ov
 * @param {KeyboardEvent} e
 */
function trapOverlayTab(ov, e) {
  if (e.key !== 'Tab') return;
  const d = doc();
  const modal = d.getElementById('ov');
  if (modal && modal.classList.contains('show')) return; // modal owns Tab
  const focusables = [...ov.querySelectorAll(FOCUSABLE)].filter(
    (el) => /** @type {any} */ (el).offsetParent !== null
  );
  const active = d.activeElement;
  const idx = focusables.indexOf(/** @type {any} */ (active));
  const outside = !ov.contains(active);
  const step = trapStep(e.shiftKey, outside ? -1 : idx, focusables.length);
  if (step === null) return;
  e.preventDefault();
  if (step === -2) ov.focus();
  else /** @type {any} */ (focusables[step]).focus();
}

function ensureOverlay() {
  const d = doc();
  if (!d) return null;
  let ov = d.getElementById('ws-overlay');
  if (!ov) {
    ov = d.createElement('div');
    ov.id = 'ws-overlay';
    ov.className = 'ws-overlay';
    ov.setAttribute('role', 'dialog');
    ov.setAttribute('aria-modal', 'true');
    ov.setAttribute('aria-label', 'Patient workspace');
    ov.setAttribute('tabindex', '-1');
    ov.hidden = true;
    ov.innerHTML =
      '<div class="ws-bar">' +
      `<button type="button" class="ws-back" data-action="ws-close" aria-label="Back">${ic(IC_BACK, 18)}<span>Back</span></button>` +
      '<span class="ws-bar-name" id="ws-bar-name"></span>' +
      '</div>' +
      '<div class="ws-scroll"><div id="ws-head" class="ws-head"></div>' +
      '<div class="ws-body"><div class="ws-main"><div id="ws-next"></div><div id="ws-tl" class="ws-tl"></div></div>' +
      '<div id="ws-side" class="ws-side"></div></div></div>';
    ov.addEventListener('keydown', (/** @type {any} */ e) => trapOverlayTab(ov, e));
    d.body.appendChild(ov);
  }
  return ov;
}

function wsSlice() {
  return store.get(SLICES.WORKSPACE) || {};
}

// ─── Renderers ─────────────────────────────────────────────────────────────

function renderAll() {
  const ov = doc() && doc().getElementById('ws-overlay');
  if (!ov || ov.hidden) return;
  const ws = wsSlice();
  const nameBar = doc().getElementById('ws-bar-name');
  if (ws.loadError) {
    renderHeadError();
    setText('ws-next', '');
    setHTML('ws-tl', '');
    setHTML('ws-side', '');
    if (nameBar) nameBar.textContent = '';
    return;
  }
  if (ws.notFound) {
    renderNotFound();
    if (nameBar) nameBar.textContent = '';
    return;
  }
  const c = ws.case;
  if (!c) return; // still loading — skeleton is already on screen
  if (nameBar) nameBar.textContent = c.patient_name || '—';
  renderHead(c, ws.panels || {});
  renderNext(c, ws.panels || {});
  renderTimeline(ws.panels || {});
  renderSide(c, ws.panels || {});
}

function setHTML(id, html) {
  const el = doc() && doc().getElementById(id);
  if (el) el.innerHTML = html;
}
function setText(id, txt) {
  const el = doc() && doc().getElementById(id);
  if (el) el.textContent = txt;
}

function renderHeadError() {
  setHTML(
    'ws-head',
    '<div class="ws-panel-err">' +
      ic(IC_WARN, 22) +
      '<div><h2>Connection problem — this patient didn’t load</h2>' +
      '<p>The record read failed (this is not a missing patient). Nothing is hidden.</p></div>' +
      '<button type="button" class="btn sm pri" data-action="ws-retry">Tap to retry</button></div>'
  );
}

function renderNotFound() {
  setText('ws-bar-name', '');
  setHTML(
    'ws-head',
    '<div class="ws-panel-err">' +
      ic(IC_WARN, 22) +
      '<div><h2>Not a case yet</h2>' +
      '<p>This looks like a consent submission that hasn’t been promoted to a case. Review it from New consents (intake lands in Phase 3).</p></div>' +
      '<button type="button" class="btn sm" data-action="ws-close">Close</button></div>'
  );
  setText('ws-next', '');
  setHTML('ws-tl', '');
  setHTML('ws-side', '');
}

function renderHead(c, panels) {
  const si = resolveStage(c);
  const stages = STAGES.map(
    (s, i) =>
      `<div class="ws-sg${i < si ? ' done' : i === si ? ' cur' : ''}"><span class="n">${String(i + 1).padStart(2, '0')}</span>` +
      `<span class="l">${esc(s.s)}</span><span class="s">${i < si ? 'Done' : i === si ? 'In progress' : 'Upcoming'}</span></div>`
  ).join('');

  const code = c.programme_code;
  const p = progFor(code);
  const progBadge = code
    ? `<span class="ws-prog">${progDotHTML(p ? p.display_name : code, programmeHex(code), 8)}<span>${esc(p ? p.display_name : code)}</span></span>`
    : '';
  const phone = c.patient_phone || '';
  const callable = phoneCallable(phone);
  const archived = isClosedCase(c);
  const facility = c.treating_facility || c.facility_name || c.county || 'No facility';

  const acts = archived
    ? `<button type="button" class="btn" data-action="ws-call"${callable ? '' : ' disabled'}>Call (read-only)</button>` +
      '<span class="ws-arch-note">Actions disabled — case archived</span>'
    : quickActionsHTML(c);

  setHTML(
    'ws-head',
    (archived ? archivedBanner(c) : '') +
      (c.legacy_hint && !archived
        ? `<div class="ws-legacy-hint">${ic(IC_NOTE, 16)}<div><b>From the paper register:</b> ${esc(c.legacy_hint)}<span class="ws-legacy-src"> · ${esc(c.legacy_source || '')}</span></div></div>`
        : '') +
      '<div class="ws-top">' +
      `<div class="ws-av" aria-hidden="true" data-avatar="${esc(avatarColor(c.patient_name))}">${esc(initials(c.patient_name))}</div>` +
      '<div class="ws-id">' +
      `<h2 class="ws-name">${esc(c.patient_name || '—')}</h2>` +
      '<div class="ws-meta">' +
      progBadge +
      `<span>${esc(c.reference_number || 'No ref')}</span>` +
      (phone
        ? callable
          ? `<button type="button" class="ws-phone" data-action="ws-call" title="Click to call">${ic(IC_PHONE, 13)}${esc(phone)}</button>`
          : `<span>${esc(phone)}</span>`
        : '') +
      `<span>${esc(facility)}</span>` +
      `<span>Enrolled ${esc(tAgo(c.created_at))}</span>` +
      (c.risk_level === 'High' || c.risk_level === 'high' ? '<span class="tag danger">High risk</span>' : '') +
      (archived ? '<span class="tag danger">Archived</span>' : '') +
      '</div>' +
      `<div class="ws-acts">${acts}</div>` +
      '</div></div>' +
      `<div class="ws-stages" role="group" aria-label="Pipeline stage">${stages}</div>`
  );
  paintAvatars('ws-head');
  void panels;
}

/**
 * Apply each avatar's deterministic colour via the CSSOM (script-driven .style
 * is NOT governed by CSP style-src, unlike an inline style= attribute parsed
 * from innerHTML which the policy blocks). @param {string} hostId
 */
function paintAvatars(hostId) {
  const host = doc() && doc().getElementById(hostId);
  if (!host) return;
  host.querySelectorAll('[data-avatar]').forEach((el) => {
    /** @type {any} */ (el).style.background = el.getAttribute('data-avatar');
  });
}

function archivedBanner(c) {
  return (
    '<div class="ws-arch">' +
    ic(IC_WARN, 16) +
    `<span>Case archived${c.archived_at ? ' · ' + new Date(c.archived_at).toLocaleDateString() : ''}</span></div>`
  );
}

function quickActionsHTML(c) {
  const callable = phoneCallable(c.patient_phone);
  const btn = (action, label, extra) =>
    `<button type="button" class="btn${extra || ''}" data-action="${action}">${label}</button>`;
  let html =
    // Re-route opens the board's stage picker (registered globally by
    // registerBoard; the modal layers above this overlay). This is THE exit
    // from the Legacy queue — a nurse must never have to hunt the 844-card
    // board for the patient they already have open.
    `<button type="button" class="btn pri" data-action="board-setstage" data-case="${esc(c.case_id)}">${ic(IC_STAGE, 14)}Re-route / Set stage</button>` +
    btn('ws-call', ic(IC_PHONE, 14) + 'Call' + (callable ? '' : ''), '') +
    btn('ws-whatsapp', ic(IC_CHAT, 14) + 'WhatsApp', '') +
    btn('ws-logcall', ic(IC_NOTE, 14) + 'Log contact', '') +
    btn('ws-clinical', ic(IC_NOTE, 14) + 'Clinical details', '') +
    btn('ws-ae', ic(IC_WARN, 14) + 'Report AE', ' danger');
  // Ownership buttons (Pick up / Assign / Reassign) by ownership + role.
  const own = workspaceOwnershipButtons(c, canAssign(role()));
  for (const k of own) {
    if (k === 'pickup') html += `<button type="button" class="btn" data-action="ws-pickup"${_pickLock ? ' disabled' : ''}>${_pickLock ? 'Picking up…' : 'Pick up'}</button>`;
    else if (k === 'assign') html += btn('ws-assign', 'Assign to…', '');
    else html += btn('ws-reassign', 'Reassign…', '');
  }
  html += `<button type="button" class="btn" data-action="ws-more" aria-label="More actions">${ic(IC_DOTS, 16)}More</button>`;
  return html;
}

function renderNext(c, panels) {
  const t = panels.tasks || {};
  if (t.error) {
    setHTML(
      'ws-next',
      '<div class="ws-panel-err sm">' +
        ic(IC_WARN, 16) +
        '<div><b>The next action didn’t load</b></div>' +
        '<button type="button" class="btn sm" data-action="ws-retry">Retry</button></div>'
    );
    return;
  }
  const open = (t.data || []).filter((x) => x.status === 'open').sort((a, b) => new Date(a.due_at).getTime() - new Date(b.due_at).getTime());
  if (!open.length) {
    setHTML(
      'ws-next',
      '<div class="ws-next none"><span class="nlbl">No open task</span><span class="ntitle">All caught up on this patient</span>' +
        (isClosedCase(c) ? '' : '<button type="button" class="btn sm" data-action="ws-logcall">Log check-in</button>') +
        '</div>'
    );
    return;
  }
  const task = open[0];
  const overdue = task.due_at && new Date(task.due_at) < new Date();
  setHTML(
    'ws-next',
    `<div class="ws-next${overdue ? ' over' : ''}"><span class="nlbl">Next action${overdue ? ' · OVERDUE' : ''}</span>` +
      `<span class="ntitle">${esc(task.title || (task.task_type || '').replace(/_/g, ' '))}</span>` +
      `<span class="nsub">Due ${esc(dueLabel(task.due_at))} ${overdue ? '— past SLA' : '— within SLA'}</span></div>`
  );
}

function renderTimeline(panels) {
  const el = doc() && doc().getElementById('ws-tl');
  if (!el) return;
  // Clinical panels drive the timeline; the audit panel degrades SILENTLY.
  const clinical = ['tasks', 'contacts', 'aes', 'deliveries', 'stageHist'];
  const errored = clinical.some((k) => panels[k] && panels[k].error);
  const src = {
    tasks: (panels.tasks || {}).data || [],
    contacts: (panels.contacts || {}).data || [],
    aes: (panels.aes || {}).data || [],
    deliveries: (panels.deliveries || {}).data || [],
    stageHist: (panels.stageHist || {}).data || [],
    assignHist: (panels.assignHist || {}).data || [], // audit — never reddens
  };
  const events = buildTimeline(src, new Date(), {
    name: (id) => userName(id),
    short: (id) => shortName(userName(id)),
  });
  const warn = errored
    ? '<div class="ws-tl-warn">' + ic(IC_WARN, 14) + '<span>Some activity couldn’t load — this timeline may be incomplete.</span>' +
      '<button type="button" class="btn sm" data-action="ws-retry">Retry</button></div>'
    : '';
  if (!events.length) {
    el.innerHTML = warn || '<div class="ws-empty"><h4>No activity yet</h4><p>Calls, deliveries and tasks form the timeline.</p></div>';
    return;
  }
  el.innerHTML =
    '<h3 class="ws-tl-h">Activity timeline</h3>' +
    warn +
    events
      .map(
        (e) =>
          `<div class="ws-tli ${esc(e.cls || '')}"><div class="ws-tl-top"><span class="ws-tl-t">${esc(e.title)}</span>` +
          `<span class="ws-tl-ts">${esc(tAgo(e.date))}</span></div>` +
          (e.desc ? `<div class="ws-tl-d">${esc(e.desc)}</div>` : '') +
          '</div>'
      )
      .join('');
}

function renderSide(c, panels) {
  const contactsPanel = panels.contacts || {};
  const cyclesPanel = panels.cycles || {};
  const assignMoves = ((panels.assignHist || {}).data || []).length;
  const cDate = (x) => x.contact_at || x.occurred_at || x.created_at;
  const lastC = (contactsPanel.data || [])
    .slice()
    .sort((a, b) => new Date(cDate(b)).getTime() - new Date(cDate(a)).getTime())[0];
  const cyc = (cyclesPanel.data || [])[0];

  const ownerName = c.assigned_to ? esc(userName(c.assigned_to)) : 'Unassigned';
  const details =
    '<div class="ws-spc"><h3 class="ws-spc-h">Patient details</h3><div class="ws-spc-b">' +
    sf('Ref', esc(c.reference_number || '—'), true) +
    sf('Phone', esc(c.patient_phone || '—')) +
    sf('County', esc(c.county || '—')) +
    sf('Facility', esc(c.treating_facility || c.facility_name || '—')) +
    sf('Owner', `${ownerName} ${assignMoves > 0 ? `<span class="ws-moves">${esc(movesLabel(assignMoves))}</span>` : ''}`) +
    sf('Risk', esc(c.risk_level || 'Standard')) +
    '</div></div>';

  const rx = cyclesPanel.error
    ? '<div class="ws-spc"><h3 class="ws-spc-h">Prescription</h3><div class="ws-spc-b"><p class="ws-danger">Could not load the prescription history.</p>' +
      '<button type="button" class="btn sm" data-action="ws-retry">Retry</button></div></div>'
    : cyc
      ? '<div class="ws-spc"><h3 class="ws-spc-h">Current prescription</h3><div class="ws-spc-b">' +
        sf('Medication', esc(cyc.medication_name || cyc.drug_name || '—')) +
        sf('Cycle', esc('#' + (cyc.cycle_no || cyc.cycle_number || '—') + ' · ' + (cyc.cycle_status || cyc.status || '—'))) +
        '</div></div>'
      : isClosedCase(c)
        ? ''
        : '<div class="ws-spc"><h3 class="ws-spc-h">Prescription</h3><div class="ws-spc-b"><p class="ws-muted">No prescription on file yet.</p></div></div>';

  const last =
    '<div class="ws-spc"><h3 class="ws-spc-h">Last contact</h3><div class="ws-spc-b">' +
    (lastC
      ? `<div class="ws-last-out">${esc((lastC.outcome || 'Contacted').replace(/_/g, ' '))}</div><div class="ws-muted">${esc(tAgo(cDate(lastC)))}</div>` +
        (lastC.notes || lastC.note ? `<div class="ws-last-note">“${esc(lastC.notes || lastC.note)}”</div>` : '')
      : contactsPanel.error
        ? '<p class="ws-danger">Could not load contacts.</p>'
        : '<p class="ws-muted">No contacts logged yet.</p>') +
    '</div></div>';

  const quick = isClosedCase(c)
    ? ''
    : '<div class="ws-spc"><h3 class="ws-spc-h">Quick actions</h3><div class="ws-spc-b ws-qa">' +
      `<button type="button" class="btn" data-action="ws-call"${phoneCallable(c.patient_phone) ? '' : ' disabled'}>${ic(IC_PHONE, 14)}Call patient</button>` +
      `<button type="button" class="btn" data-action="ws-whatsapp">${ic(IC_CHAT, 14)}WhatsApp</button>` +
      `<button type="button" class="btn" data-action="ws-message">${ic(IC_NOTE, 14)}SMS / Email</button>` +
      `<button type="button" class="btn" data-action="ws-logcall">${ic(IC_NOTE, 14)}Log call</button>` +
      '</div></div>';

  setHTML('ws-side', details + rx + last + quick + legacyPanelHTML(c, panels) + notesPanelHTML(c));
}

/**
 * Record notes — the case's intake_notes, verbatim. This is where data
 * provenance lives: every merge writes its story here ("MERGED with
 * consent-form record …", "HISTORY-PAGE ENTRY MERGED …", "CONSENT RESOLVED …")
 * precisely so a future reader — founder or nurse — can see what was changed,
 * when, and under whose decision, and amend the record if a merge turns out
 * to be wrong. Renders for ARCHIVED cases too, deliberately: the history-page
 * merges live entirely in Archived, and provenance that hides on archived
 * records is provenance nobody will ever read. Pure — exported for tests.
 * @param {any} c @returns {string}
 */
export function notesPanelHTML(c) {
  const t = String((c && c.intake_notes) || '').trim();
  if (!t) return '';
  return (
    '<div class="ws-spc"><h3 class="ws-spc-h">Record notes</h3><div class="ws-spc-b">' +
    `<div class="ws-notes">${esc(t)}</div>` +
    '<p class="ws-notes-cap">These notes are the audit trail of how this record was assembled ' +
    '(register load, merges, decisions). If anything looks wrong, correct the record and log a ' +
    'call describing the fix.</p>' +
    '</div></div>'
  );
}

// ─── Legacy register history (2024-26 migration) ───────────────────────────

/**
 * 'YYYY-MM' or YYYYMM (the legacy_episodes.month_key int, e.g. 202403) →
 * 'Jul 2026'; anything unparseable comes back verbatim so an odd source tab
 * name still shows SOMETHING rather than 'Invalid Date'. Pure.
 * @param {string|number} key @returns {string}
 */
export function monthLabel(key) {
  const m = /^(\d{4})-?(\d{2})$/.exec(String(key || ''));
  if (!m) return String(key || '—');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const mi = Number(m[2]) - 1;
  return mi >= 0 && mi < 12 ? `${names[mi]} ${m[1]}` : String(key);
}

/**
 * Group legacy episodes by month, newest first (rows arrive month_key desc from
 * the loader but grouping re-sorts defensively). Pure — unit-tested.
 * @param {any[]} rows legacy_episodes rows
 * @returns {Array<{key: string, label: string, rows: any[]}>}
 */
export function groupEpisodes(rows) {
  /** @type {Record<string, any[]>} */
  const by = {};
  for (const r of rows || []) {
    const k = String((r && r.month_key) || '—');
    (by[k] || (by[k] = [])).push(r);
  }
  return Object.keys(by)
    .sort()
    .reverse()
    .map((k) => ({ key: k, label: monthLabel(k), rows: by[k] }));
}

/**
 * The register-history side panel. Renders ONLY for migrated cases
 * (legacy_source set) — everyone else never sees it and the table is never
 * queried for them. Every register cell the founder asked to preserve is
 * shown under its month: medication, packs, approval, dates, and the verbatim
 * comment ("capture all the comments under specific months … nothing is lost").
 * @param {any} c @param {Record<string, any>} panels @returns {string}
 */
function legacyPanelHTML(c, panels) {
  if (!c.legacy_source) return '';
  const p = panels.episodes || {};
  if (p.error) {
    return '<div class="ws-spc"><h3 class="ws-spc-h">Register history</h3><div class="ws-spc-b">' +
      '<p class="ws-danger">Could not load the register history.</p>' +
      '<button type="button" class="btn sm" data-action="ws-retry">Retry</button></div></div>';
  }
  const rows = p.data || [];
  const head =
    sf('Register', esc(String(c.legacy_source).split(':')[0])) +
    (c.legacy_code && c.legacy_code !== 'HIST' ? sf('Code', esc(c.legacy_code), true) : '') +
    sf('Entries', esc(String(rows.length)));
  if (!rows.length) {
    return '<div class="ws-spc"><h3 class="ws-spc-h">Register history</h3><div class="ws-spc-b">' + head +
      '<p class="ws-muted">No month entries were recorded for this patient.</p></div></div>';
  }
  const months = groupEpisodes(rows)
    .map((g) => {
      const items = g.rows
        .map((r) => {
          const bits = [];
          if (r.medication) bits.push(esc(r.medication));
          if (r.packs) bits.push(esc(String(r.packs)) + ' pack' + (String(r.packs) === '1' ? '' : 's'));
          if (r.approval_status) bits.push(esc(r.approval_status));
          if (r.refill_date) bits.push('refill ' + esc(r.refill_date));
          else if (r.dispatch_date) bits.push('dispatched ' + esc(r.dispatch_date));
          else if (r.start_date) bits.push('started ' + esc(r.start_date));
          return (
            '<div class="ws-ep">' +
            `<div class="ws-ep-l">${bits.join(' · ') || 'Entry ' + esc(String(r.episode_no || ''))}</div>` +
            (r.comments ? `<div class="ws-ep-c">“${esc(r.comments)}”</div>` : '') +
            '</div>'
          );
        })
        .join('');
      return `<div class="ws-ep-m"><h4 class="ws-ep-mh">${esc(g.label)}</h4>${items}</div>`;
    })
    .join('');
  return (
    '<div class="ws-spc"><h3 class="ws-spc-h">Register history</h3>' +
    `<div class="ws-spc-b">${head}<div class="ws-ep-list">${months}</div></div></div>`
  );
}

/** One side-panel field row. */
function sf(k, vHTML, mono) {
  return `<div class="ws-sf"><span class="ws-sf-k">${esc(k)}</span><span class="ws-sf-v${mono ? ' mono' : ''}">${vHTML}</span></div>`;
}

// ─── Call (honest — no fake recording) ──────────────────────────────────────

function currentCase() {
  return wsSlice().case;
}

function isMobile() {
  const w = /** @type {any} */ (globalThis);
  return /Android|iPhone|iPad|iPod/i.test((w.navigator && w.navigator.userAgent) || '') || (w.innerWidth || 1024) <= 768;
}

function onCall() {
  const c = currentCase();
  const phone = c && c.patient_phone;
  if (!phoneCallable(phone)) {
    toast('No phone number on file', 'err');
    return;
  }
  if (isMobile()) {
    /** @type {any} */ (globalThis).location.href = 'tel:' + phone;
    return;
  }
  // Desktop: no dialer — show the number to call from the nurse's own handset,
  // then log the outcome. NEVER a fake "recording" (legacy honest call).
  const body =
    '<div class="ws-call-num">' +
    `<button type="button" class="ws-call-copy" data-action="ws-copy-number" data-num="${esc(phone)}" title="Click to copy">${esc(phone)}</button>` +
    '<p class="ws-muted">Dial this number from your phone, then log the outcome.</p></div>';
  openModal('Call ' + (c.patient_name || 'patient'), '', body, [
    { l: 'Cancel', k: 'cancel', fn: () => closeModal() },
    { l: 'Log call', v: 'pri', k: 'log', fn: () => { closeModal(); openCallLog(); } },
  ]);
}

function onCopyNumber(el) {
  const num = el.getAttribute('data-num') || '';
  const nav = /** @type {any} */ (globalThis).navigator;
  if (nav && nav.clipboard && nav.clipboard.writeText) {
    nav.clipboard.writeText(num).then(() => toast('Number copied', 'ok')).catch(() => {});
  }
}

// ─── Log call (real contacts write) ────────────────────────────────────────

const CALL_OUTCOMES = [
  { v: 'reached_patient', l: 'Reached patient' },
  { v: 'reached_caregiver', l: 'Reached caregiver' },
  { v: 'no_answer', l: 'No answer' },
  { v: 'switched_off', l: 'Phone off / unreachable' },
  { v: 'wrong_number', l: 'Wrong number' },
];

// Which channel the attempt went out on. This used to be hard-coded to 'call' on
// every save, which made "reach rate by channel" a single bar and made it
// impossible to tell whether an attempt honoured the patient's consented
// channel — a data-protection question, not just a service one. The values match
// the comms_consent keys exactly so the two can be compared without a lookup.
const CONTACT_CHANNELS = [
  { v: 'call', l: 'Phone call' },
  { v: 'sms', l: 'SMS' },
  { v: 'whatsapp', l: 'WhatsApp' },
  { v: 'email', l: 'Email' },
  { v: 'in_person', l: 'In person' },
];

function openCallLog() {
  const c = currentCase();
  if (!c) return;
  const opts = CALL_OUTCOMES.map((o) => `<option value="${esc(o.v)}">${esc(o.l)}</option>`).join('');
  // Mark the channels this patient actually consented to, so a nurse can see at a
  // glance which ones are permitted before choosing — the report measures exactly
  // this, and it is cheaper to prevent an off-consent contact than to report it.
  const cc = (c && c.comms_consent) || {};
  const chOpts = CONTACT_CHANNELS.map(
    (o) => `<option value="${esc(o.v)}">${esc(o.l)}${cc[o.v] === true ? ' ✓ consented' : cc[o.v] === false ? ' — not consented' : ''}</option>`
  ).join('');
  const body =
    '<div class="field"><label>Channel *</label>' +
    `<select id="ws-call-channel">${chOpts}</select></div>` +
    '<div class="field"><label>Outcome *</label>' +
    `<select id="ws-call-outcome">${opts}</select></div>` +
    '<div class="field"><label>Note</label>' +
    '<textarea id="ws-call-note" rows="3" placeholder="What did you discuss? Any follow-up needed?"></textarea></div>';
  openModal('Log contact', c.patient_name || '', body, [
    { l: 'Cancel', k: 'cancel', fn: () => closeModal() },
    { l: 'Save', v: 'pri', k: 'save', fn: () => submitCallLog(c.case_id) },
  ]);
}

async function submitCallLog(caseId) {
  const d = doc();
  const outcome = (d.getElementById('ws-call-outcome') || {}).value;
  const channel = (d.getElementById('ws-call-channel') || {}).value || 'call';
  const note = ((d.getElementById('ws-call-note') || {}).value || '').trim();
  if (!outcome) {
    toast('Choose an outcome', 'err');
    return;
  }
  setBtnBusy(true, 'Saving…', 'save');
  const res = await logContact(caseId, { channel, outcome, note }, undefined);
  if (!res.ok) {
    setBtnBusy(false);
    toast(netErr(res.error, 'Could not save the contact'), 'err');
    return;
  }
  closeModal();
  toast('Contact logged', 'ok');
  await reload();
}

// ─── Clinical details (diagnosis · stage · delivery route) ──────────────────
//
// These three columns have been on `cases` since the baseline but had no capture
// surface in either app: the consent form never asked, and the workspace had no
// edit. So every report tile reading them honestly said "not recorded" for every
// patient. Values come from the shared vocabularies in db/contract.js so the
// report can band on them — free-typed variants would split one cohort into
// several bars. Diagnosis stays free text (there is no sane fixed list) and is
// therefore the one field the reports tile warns can fragment.

function openClinicalDetails() {
  const c = currentCase();
  if (!c) return;
  if (isClosedCase(c)) {
    toast('Case is archived — actions disabled', 'err');
    return;
  }
  const sel = (list, current) =>
    '<option value="">— not recorded —</option>' +
    list.map((o) => `<option value="${esc(o.v)}"${String(current || '') === o.v ? ' selected' : ''}>${esc(o.l)}</option>`).join('');
  const body =
    '<div class="field"><label for="ws-cd-dx">Diagnosis</label>' +
    `<input id="ws-cd-dx" type="text" maxlength="120" value="${esc(c.diagnosis || '')}" placeholder="e.g. Breast cancer"></div>` +
    '<div class="field"><label for="ws-cd-stage">Cancer stage at enrolment</label>' +
    `<select id="ws-cd-stage">${sel(CANCER_STAGES, c.cancer_stage)}</select></div>` +
    '<div class="field"><label for="ws-cd-delivery">How medicine reaches the patient</label>' +
    `<select id="ws-cd-delivery">${sel(DELIVERY_METHODS, c.delivery_method)}</select></div>` +
    '<p class="mut sm">Leave a field blank to leave it unchanged. These three feed the enrolment and retention reports.</p>';
  openModal('Clinical details', c.patient_name || '', body, [
    { l: 'Cancel', k: 'cancel', fn: () => closeModal() },
    { l: 'Save', v: 'pri', k: 'save', fn: () => submitClinicalDetails(c.case_id) },
  ]);
}

async function submitClinicalDetails(caseId) {
  const d = doc();
  const form = {
    diagnosis: ((d.getElementById('ws-cd-dx') || {}).value || '').trim(),
    cancer_stage: (d.getElementById('ws-cd-stage') || {}).value || '',
    delivery_method: (d.getElementById('ws-cd-delivery') || {}).value || '',
  };
  if (!form.diagnosis && !form.cancer_stage && !form.delivery_method) {
    toast('Nothing to save — fill at least one field', 'err');
    return;
  }
  setBtnBusy(true, 'Saving…', 'save');
  const res = await updateClinicalDetails(caseId, form, undefined);
  if (!res.ok) {
    setBtnBusy(false);
    toast(netErr(res.error, 'Could not save the clinical details'), 'err');
    return;
  }
  closeModal();
  toast('Clinical details saved', 'ok');
  await reload();
}

// ─── Report AE (F-AE-*) ─────────────────────────────────────────────────────

function openAEForm() {
  const c = currentCase();
  if (!c) return;
  if (isClosedCase(c)) {
    toast('Case is archived — actions disabled', 'err');
    return;
  }
  const p = progFor(c.programme_code);
  const defaultSuspect = p && p.default_drug_name ? p.default_drug_name + (p.default_strength ? ' ' + p.default_strength : '') : SUSPECT_DEFAULT;
  const aeInbox = (p && p.ae_team_email) || AE_EMAIL.to;
  const today = new Date().toISOString().slice(0, 10);
  const sevOpts = AE_SEVERITY.map((s) => `<option value="${esc(s.v)}"${s.v === 'moderate' ? ' selected' : ''}>${esc(s.l)}</option>`).join('');
  const outOpts = AE_OUTCOME.map((o) => `<option value="${esc(o.v)}"${o.v === 'recovering' ? ' selected' : ''}>${esc(o.l)}</option>`).join('');
  const critBoxes = AE_SERIOUSNESS_CRITERIA.map(
    (cr) => `<label class="ws-ae-crit"><input type="checkbox" class="ws-ae-crit-box" value="${esc(cr.v)}"/> ${esc(cr.l)}</label>`
  ).join('');
  const body =
    '<div class="ws-ae">' +
    '<div class="field"><label>Reaction term (MedDRA-style) *</label>' +
    '<input id="ws-ae-term" placeholder="e.g. Nausea and vomiting, Hepatotoxicity, Rash"/></div>' +
    `<div class="field"><label>Date of onset *</label><input id="ws-ae-onset" type="date" value="${esc(today)}" max="${esc(today)}"/></div>` +
    '<div class="field"><label>Seriousness *</label>' +
    '<select id="ws-ae-ser"><option value="non_serious">Non-serious</option><option value="serious">SERIOUS (see criteria)</option></select></div>' +
    `<div class="field" id="ws-ae-crit-wrap" hidden><label>Seriousness criteria (tick all that apply)</label><div class="ws-ae-crits">${critBoxes}</div></div>` +
    `<div class="field"><label>Severity</label><select id="ws-ae-sev">${sevOpts}</select></div>` +
    `<div class="field"><label>Outcome</label><select id="ws-ae-out">${outOpts}</select></div>` +
    `<div class="field"><label>Suspect product(s)</label><input id="ws-ae-sus" value="${esc(defaultSuspect)}" placeholder="Drug, strength, route"/></div>` +
    '<div class="field"><label>Concomitant medications</label><input id="ws-ae-co" placeholder="e.g. Prednisone 5mg BID, Tamsulosin 0.4mg OD"/></div>' +
    '<div class="field"><label>Action taken</label><input id="ws-ae-act" placeholder="e.g. Drug withheld, hospitalised for hydration"/></div>' +
    '<div class="field"><label>Full narrative *</label>' +
    '<textarea id="ws-ae-desc" rows="4" placeholder="Timeline, symptoms, severity progression, patient response, follow-up plan."></textarea></div>' +
    '<div class="ws-ae-notice">' +
    ic(IC_WARN, 14) +
    `<span>On submit, an identifiable PV report is drafted to <b>${esc(aeInbox)}</b> in your email client. Nothing is sent automatically — you send it, then confirm so the reconciliation record is stamped.</span>` +
    '</div>' + // close .ws-ae-notice
    '</div>'; // close .ws-ae
  openModal('Report adverse event', `${c.patient_name || 'Patient'} · ${c.reference_number || '—'}`, body, [
    { l: 'Cancel', k: 'cancel', fn: () => closeModal() },
    { l: 'Submit & draft email', v: 'danger', k: 'submit', fn: () => submitAE(c) },
  ], true);
}

function onAESerChange() {
  const d = doc();
  const ser = (d.getElementById('ws-ae-ser') || {}).value;
  const wrap = d.getElementById('ws-ae-crit-wrap');
  if (wrap) wrap.hidden = ser !== 'serious';
}

function readAEForm() {
  const d = doc();
  const val = (id) => ((d.getElementById(id) || {}).value || '').trim();
  const crits = [...d.querySelectorAll('.ws-ae-crit-box')].filter((b) => b.checked).map((b) => b.value);
  return {
    reaction_term: val('ws-ae-term'),
    onset_date: val('ws-ae-onset'),
    seriousness: (d.getElementById('ws-ae-ser') || {}).value,
    severity: (d.getElementById('ws-ae-sev') || {}).value,
    outcome: (d.getElementById('ws-ae-out') || {}).value,
    ae_description: val('ws-ae-desc'),
    suspect_products: (val('ws-ae-sus') || SUSPECT_DEFAULT).split(',').map((s) => s.trim()).filter(Boolean),
    co_medications: val('ws-ae-co') || null,
    action_taken: val('ws-ae-act') || null,
    seriousness_criteria: crits,
  };
}

async function submitAE(c) {
  const form = readAEForm();
  const bad = validateAE(form);
  if (bad) {
    toast(bad, 'err'); // modal stays open, typed input intact
    return;
  }
  setBtnBusy(true, 'Saving…', 'submit');
  const res = await reportAdverseEvent(c.case_id, form, undefined);
  if (!res.ok) {
    setBtnBusy(false);
    toast(netErr(res.error, 'Could not save the adverse event'), 'err');
    return;
  }
  closeModal();
  const result = res.data || {};
  // Compose the identifiable PV report and open the nurse's mail client (mailto).
  // NO automatic send — the human sends it, then confirms (reconciliation).
  const sess = session();
  const subject = aeEmailSubject(form, result);
  const body = buildAEEmailBody(result, form, c, { name: sess.fullName, role: sess.role, email: sess.user && sess.user.email });
  const { url, to } = buildMailto(result, subject, body);
  openMailto(url);
  toast('AE saved · draft opened in your email client', 'ok');
  await reload();
  setTimeout(() => openAEMarkSent(result.ae_id, subject, body, to), 600);
}

function openMailto(url) {
  const d = doc();
  if (!d) return;
  const a = d.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener';
  d.body.appendChild(a);
  a.click();
  d.body.removeChild(a);
}

let _aeBody = '';
function openAEMarkSent(aeId, subject, body, to) {
  if (!aeId) return; // no server row → nothing to reconcile
  _aeBody = body;
  const preview = (body || '').slice(0, 700);
  const html =
    '<div class="ws-ae-sent">' +
    `<p class="ws-ae-ok">Draft opened for <b>${esc(to)}</b>. Click <b>Send</b> in your email client, then come back and confirm — this stamps the reconciliation record so we know the PV team was notified.</p>` +
    '<div class="field"><label>Subject (read-only)</label>' +
    `<input value="${esc(subject)}" readonly class="ws-mono"/></div>` +
    '<div class="field"><label>Body preview</label>' +
    `<textarea readonly rows="8" class="ws-mono">${esc(preview)}</textarea>` +
    '<p class="ws-muted">If nothing opened, <button type="button" class="ws-linkbtn" data-action="ws-ae-copy">copy the body</button> and paste it into your email manually.</p></div>' +
    '<div class="field"><label>(Optional) AE team reference number</label>' +
    '<input id="ws-ae-ref" placeholder="e.g. PV-2026-00123 — if they replied with one"/></div>' +
    '</div>';
  openModal('Email drafted', 'Confirm once you’ve sent it', html, [
    { l: 'I’ll send later', k: 'later', fn: () => closeModal() },
    { l: 'Mark email sent', v: 'pri', k: 'marksent', fn: () => submitMarkSent(aeId) },
  ], true);
}

function onAECopy() {
  const nav = /** @type {any} */ (globalThis).navigator;
  if (nav && nav.clipboard && nav.clipboard.writeText) {
    nav.clipboard.writeText(_aeBody || '').then(() => toast('Copied to clipboard', 'ok')).catch(() => {});
  }
}

async function submitMarkSent(aeId) {
  const d = doc();
  const ref = ((d.getElementById('ws-ae-ref') || {}).value || '').trim() || null;
  setBtnBusy(true, 'Saving…', 'marksent');
  const res = await markAENotified(aeId, { teamReference: ref }, undefined);
  if (!res.ok) {
    setBtnBusy(false);
    toast(netErr(res.error, 'Could not mark the email sent'), 'err');
    return;
  }
  closeModal();
  toast('AE marked as notified · reconciliation updated', 'ok');
}

// ─── Message composer (bilingual EN/SW; R-016) ──────────────────────────────

function msgVars(c) {
  const p = progFor(c.programme_code);
  return {
    name: c.patient_name ? String(c.patient_name).split(/\s+/)[0] : '',
    ref: c.reference_number || '',
    nextDate: '', // filled when a refill/next date is available (Phase 4 cycles)
    programme: p ? p.display_name : 'Tumaini',
    nurse: session().fullName || 'your care team',
  };
}

function openComposer() {
  const c = currentCase();
  if (!c) return;
  _compPhone = c.patient_phone || '';
  _compTpl = null;
  _compLastGen = '';
  _compLang = pickLanguage(c, readLastLang(selfId()));
  openModal('Message ' + (c.patient_name || 'patient'), 'Draft, then Copy for SMS or open in WhatsApp — nothing sends automatically', composerBodyHTML(c), [
    { l: 'Close', k: 'close', fn: () => closeModal() },
  ], true);
}

function composerBodyHTML(c) {
  const langToggle =
    '<div class="ws-lang" role="group" aria-label="Message language">' +
    (['en', 'sw'])
      .map(
        (l) =>
          `<button type="button" class="ws-lang-btn${_compLang === l ? ' on' : ''}" data-action="ws-msg-lang" data-lang="${l}" aria-pressed="${_compLang === l}">${esc(LANG_LABEL[l])}</button>`
      )
      .join('') +
    '</div>';
  const chips = MESSAGE_TEMPLATES.map(
    (t) =>
      `<button type="button" class="ws-tpl-chip${_compTpl === t.id ? ' on' : ''}" data-action="ws-msg-tpl" data-tpl="${esc(t.id)}">${esc(templateLabel(t.id, _compLang))}</button>`
  ).join('');
  const pillLine = ['call', 'sms', 'whatsapp', 'email']
    .map((ch) => {
      const pref = commsPref(c, ch);
      const g = pref === 'unknown' ? '—' : pref === 'yes' ? '✓' : '✗';
      return `${ch[0].toUpperCase() + ch.slice(1)} ${g}`;
    })
    .join(' · ');
  return (
    '<div class="ws-comp">' +
    `<div class="ws-comp-consent" title="✓ opted in · ✗ declined · — not captured">${esc('Consent: ' + pillLine)}</div>` +
    '<div class="ws-comp-row">' +
    langToggle +
    `<div class="ws-tpl-chips">${chips}</div>` +
    '</div>' +
    '<div id="ws-comp-notice"></div>' +
    '<div class="field"><label>Message</label>' +
    '<textarea id="ws-msg-text" rows="5" placeholder="Pick a template above, or write your own…"></textarea>' +
    '<div class="ws-comp-meta"><span id="ws-msg-count">0 chars · 0 SMS</span>' +
    `<span class="ws-comp-to">To: ${esc(_compPhone || '—')}</span></div></div>` +
    '<div class="ws-comp-acts">' +
    `<button type="button" class="btn" data-action="ws-msg-copy">Copy for SMS</button>` +
    (phoneCallable(_compPhone) ? `<button type="button" class="btn pri" data-action="ws-msg-whatsapp">Open in WhatsApp</button>` : '') +
    '</div></div>'
  );
}

function composerTextEl() {
  return doc() && doc().getElementById('ws-msg-text');
}

function updateMsgMeta() {
  const el = composerTextEl();
  const meta = doc() && doc().getElementById('ws-msg-count');
  if (!el || !meta) return;
  const n = (el.value || '').length;
  const units = Math.ceil(n / 160) || 0;
  meta.textContent = `${n} chars · ${units} SMS unit${units === 1 ? '' : 's'}`;
}

function fillTemplate(tplId, lang) {
  const c = currentCase();
  const text = renderTemplate(tplId, lang, msgVars(c));
  const el = composerTextEl();
  if (el) el.value = text;
  _compLastGen = text;
  _compTpl = tplId;
  _compLang = lang;
  clearComposerNotice();
  refreshComposerChrome();
  updateMsgMeta();
}

function clearComposerNotice() {
  const n = doc() && doc().getElementById('ws-comp-notice');
  if (n) n.innerHTML = '';
}

/** Re-render the language buttons + template chips (their labels + active state). */
function refreshComposerChrome() {
  const c = currentCase();
  if (!c) return;
  const row = doc() && doc().querySelector('.ws-comp-row');
  if (!row) return;
  const langToggle = row.querySelector('.ws-lang');
  const chips = row.querySelector('.ws-tpl-chips');
  if (langToggle) {
    langToggle.querySelectorAll('.ws-lang-btn').forEach((b) => {
      const on = b.getAttribute('data-lang') === _compLang;
      b.classList.toggle('on', on);
      b.setAttribute('aria-pressed', String(on));
    });
  }
  if (chips) {
    chips.innerHTML = MESSAGE_TEMPLATES.map(
      (t) =>
        `<button type="button" class="ws-tpl-chip${_compTpl === t.id ? ' on' : ''}" data-action="ws-msg-tpl" data-tpl="${esc(t.id)}">${esc(templateLabel(t.id, _compLang))}</button>`
    ).join('');
  }
}

function onMsgTpl(el) {
  const id = el.getAttribute('data-tpl');
  if (!id) return;
  const cur = composerTextEl();
  // Switching template over a DIRTY draft would discard nurse edits — offer an
  // explicit replace instead of silently overwriting (v20.1 guard).
  if (cur && draftIsDirty(cur.value, _compLastGen)) {
    showReplaceNotice('template', id, _compLang);
    return;
  }
  fillTemplate(id, _compLang);
}

function onMsgLang(el) {
  const lang = /** @type {'en'|'sw'} */ (el.getAttribute('data-lang') === 'sw' ? 'sw' : 'en');
  writeLastLang(selfId(), lang);
  const cur = composerTextEl();
  // A language flip must NOT silently discard edits. If a template is selected
  // and the draft is dirty, keep the text and offer to replace with the new
  // language; otherwise regenerate (or just switch chip labels if no template).
  if (_compTpl && cur && draftIsDirty(cur.value, _compLastGen)) {
    _compLang = lang; // chip labels follow the chosen language
    refreshComposerChrome();
    showReplaceNotice('lang', _compTpl, lang);
    return;
  }
  if (_compTpl) {
    fillTemplate(_compTpl, lang);
  } else {
    _compLang = lang;
    refreshComposerChrome();
  }
}

function showReplaceNotice(kind, tplId, lang) {
  const n = doc() && doc().getElementById('ws-comp-notice');
  if (!n) return;
  const what = kind === 'lang' ? `the ${LANG_LABEL[lang]} version` : `“${esc(templateLabel(tplId, lang))}”`;
  n.innerHTML =
    '<div class="ws-comp-warn">' +
    ic(IC_WARN, 14) +
    `<span>You’ve edited this draft. Replace it with ${what}?</span>` +
    `<button type="button" class="btn sm" data-action="ws-msg-replace" data-tpl="${esc(tplId)}" data-lang="${esc(lang)}">Replace</button>` +
    '<button type="button" class="btn sm" data-action="ws-msg-keep">Keep my draft</button>' +
    '</div>';
}

function onMsgReplace(el) {
  const id = el.getAttribute('data-tpl');
  const lang = /** @type {'en'|'sw'} */ (el.getAttribute('data-lang') === 'sw' ? 'sw' : 'en');
  if (id) fillTemplate(id, lang);
}

function onMsgKeep() {
  clearComposerNotice();
}

function onMsgCopy() {
  const c = currentCase();
  guardComms(c, 'sms', 'SMS', () => {
    const el = composerTextEl();
    const nav = /** @type {any} */ (globalThis).navigator;
    if (el && nav && nav.clipboard && nav.clipboard.writeText) {
      nav.clipboard.writeText(el.value || '').then(() => toast('Copied — paste into your SMS app', 'ok')).catch(() => {});
    }
  });
}

function onMsgWhatsapp() {
  const c = currentCase();
  guardComms(c, 'whatsapp', 'WhatsApp', () => {
    const el = composerTextEl();
    const clean = String(_compPhone || '').replace(/\D/g, '');
    if (!clean) {
      toast('No phone number on file', 'err');
      return;
    }
    const url = 'https://wa.me/' + clean + '?text=' + encodeURIComponent((el && el.value) || '');
    /** @type {any} */ (globalThis).open(url, '_blank', 'noopener');
  });
}

/**
 * Comms-consent gate (legacy guardComms, CSP-safe): if the patient DECLINED this
 * channel on their consent form, do NOT proceed silently — show an inline
 * confirm inside the composer and only run `proceed` on an explicit override.
 */
let _pendingComms = /** @type {(() => void)|null} */ (null);
function guardComms(c, channel, label, proceed) {
  if (commsPref(c, channel) === 'no') {
    _pendingComms = proceed;
    const n = doc() && doc().getElementById('ws-comp-notice');
    if (n) {
      n.innerHTML =
        '<div class="ws-comp-warn">' +
        ic(IC_WARN, 14) +
        `<span>The patient declined ${esc(label)} contact on their consent form.</span>` +
        '<button type="button" class="btn sm danger" data-action="ws-comms-yes">Contact anyway</button>' +
        '<button type="button" class="btn sm" data-action="ws-comms-no">Cancel</button>' +
        '</div>';
    }
    return;
  }
  proceed();
}
function onCommsYes() {
  const p = _pendingComms;
  _pendingComms = null;
  clearComposerNotice();
  if (p) p();
}
function onCommsNo() {
  _pendingComms = null;
  clearComposerNotice();
}

// ─── More actions ───────────────────────────────────────────────────────────

function openMore() {
  const c = currentCase();
  if (!c) return;
  if (isClosedCase(c)) {
    toast('Case is archived — actions disabled', 'err');
    return;
  }
  const body =
    '<div class="ws-more">' +
    '<button type="button" class="btn ws-more-item" data-action="ws-logcall"><span>Log call</span><span class="ws-more-d">Record the outcome of a call you placed</span></button>' +
    '<button type="button" class="btn ws-more-item" data-action="ws-message"><span>SMS / Email</span><span class="ws-more-d">Draft a patient message to copy or send</span></button>' +
    '<button type="button" class="btn ws-more-item danger" data-action="ws-ae"><span>Report AE</span><span class="ws-more-d">Report an adverse event to pharmacovigilance</span></button>' +
    '<button type="button" class="btn ws-more-item danger" data-action="ws-archive"><span>Archive &amp; close case</span><span class="ws-more-d">Capture the outcome and close this case (R-024)</span></button>' +
    '<p class="ws-muted ws-more-note">Prescription, delivery, escalation and notes land later in Phase 4.</p>' +
    '</div>';
  openModal('More actions', c.patient_name || '', body, [{ l: 'Close', k: 'close', fn: () => closeModal() }]);
}

function moreThenClose(fn) {
  closeModal();
  fn();
}

// ─── Open / close / reload ─────────────────────────────────────────────────

/**
 * Open the workspace overlay for a case. Latest-wins: a newer open supersedes an
 * in-flight one. Never throws.
 * @param {string} caseId
 * @returns {Promise<void>}
 */
export async function openWorkspace(caseId) {
  if (!caseId || caseId === 'undefined') return;
  wireOnce();
  const ov = ensureOverlay();
  if (!ov) return;
  const d = doc();
  if (ov.hidden) _prevFocus = d.activeElement; // remember the trigger for restore
  _openId = caseId;
  ov.hidden = false;
  d.body.classList.add('ws-open');
  // Skeleton while it loads.
  setText('ws-bar-name', 'Loading…');
  setHTML('ws-head', '<div class="ws-skel"><div class="ws-skel-l lg"></div><div class="ws-skel-l"></div></div>');
  setText('ws-next', '');
  setHTML('ws-tl', '<div class="ws-skel"><div class="ws-skel-l"></div><div class="ws-skel-l"></div></div>');
  setHTML('ws-side', '');
  ov.focus();
  const isStale = _ws.begin();
  const res = await loadWorkspace(caseId, { isStale });
  if (res.code === 'STALE') return; // a newer open won
  renderAll();
}

/** Close the overlay and restore focus to the trigger. @returns {void} */
export function closeWorkspace() {
  const d = doc();
  const ov = d && d.getElementById('ws-overlay');
  if (!ov) return;
  ov.hidden = true;
  d.body.classList.remove('ws-open');
  _openId = null;
  if (_prevFocus && /** @type {any} */ (_prevFocus).focus) {
    try {
      /** @type {any} */ (_prevFocus).focus();
    } catch {
      /* trigger gone */
    }
  }
  _prevFocus = null;
}

/** Is the overlay currently open on a case? */
function isOpen() {
  const ov = doc() && doc().getElementById('ws-overlay');
  return !!ov && !ov.hidden && !!_openId;
}

async function reload() {
  if (!_openId) return;
  const isStale = _ws.begin();
  const res = await loadWorkspace(_openId, { isStale });
  if (res.code === 'STALE') return;
  renderAll();
}

// ─── Wiring (once) ──────────────────────────────────────────────────────────

function wireOnce() {
  if (_wired) return;
  _wired = true;
  const d = doc();

  register('ws-close', () => closeWorkspace());
  register('ws-retry', () => void reload());
  register('ws-call', () => onCall());
  register('ws-copy-number', (el) => onCopyNumber(el));
  register('ws-logcall', (el) => (el.closest('#ov') ? moreThenClose(openCallLog) : openCallLog()));
  register('ws-clinical', (el) => (el.closest('#ov') ? moreThenClose(openClinicalDetails) : openClinicalDetails()));
  register('ws-ae', (el) => (el.closest('#ov') ? moreThenClose(openAEForm) : openAEForm()));
  register('ws-ae-copy', () => onAECopy());
  register('ws-whatsapp', () => openComposer());
  register('ws-message', (el) => (el.closest('#ov') ? moreThenClose(openComposer) : openComposer()));
  register('ws-more', () => openMore());
  register('ws-archive', (el) => (el.closest('#ov') ? moreThenClose(onArchive) : onArchive()));
  register('ws-pickup', () => void onPickup());
  register('ws-assign', () => onAssign());
  register('ws-reassign', () => onReassign());

  // Composer actions.
  register('ws-msg-lang', (el) => onMsgLang(el));
  register('ws-msg-tpl', (el) => onMsgTpl(el));
  register('ws-msg-replace', (el) => onMsgReplace(el));
  register('ws-msg-keep', () => onMsgKeep());
  register('ws-msg-copy', () => onMsgCopy());
  register('ws-msg-whatsapp', () => onMsgWhatsapp());
  register('ws-comms-yes', () => onCommsYes());
  register('ws-comms-no', () => onCommsNo());

  // The AE seriousness select fires on change (not click) — wire a change listener.
  if (d) {
    d.addEventListener('change', (/** @type {any} */ e) => {
      const t = e.target;
      if (t && t.id === 'ws-ae-ser') onAESerChange();
    });
    // Live SMS counter as the nurse types.
    d.addEventListener('input', (/** @type {any} */ e) => {
      const t = e.target;
      if (t && t.id === 'ws-msg-text') updateMsgMeta();
    });
    // Escape closes the workspace — but only when no modal sits on top of it
    // (the modal owns Escape first, via main.js's bubble-phase listener).
    d.addEventListener('keydown', (/** @type {any} */ e) => {
      if (e.key !== 'Escape' || !isOpen()) return;
      const modal = d.getElementById('ov');
      if (modal && modal.classList.contains('show')) return; // modal handles it
      closeWorkspace();
    });
  }

  // Re-render on WORKSPACE slice changes.
  store.subscribe(SLICES.WORKSPACE, () => {
    if (isOpen()) renderAll();
  });
  // Realtime: reload the open patient when cases/tasks change (legacy did the
  // same on the open workspace). RT_CHANGES flags 'patients'/'inbox' for cases.
  store.subscribe(SLICES.RT_CHANGES, (v) => {
    if (isOpen() && v && Array.isArray(v.changed) && (v.changed.includes('patients') || v.changed.includes('inbox'))) {
      void reload();
    }
  });
}

async function onPickup() {
  if (_pickLock || !_openId) return;
  const caseId = _openId;
  _pickLock = true;
  renderAll();
  await pickUp(caseId, { onDone: async () => { _pickLock = false; await reload(); } });
  _pickLock = false;
}

function onAssign() {
  const c = currentCase();
  if (!c) return;
  openAssignModal(c.case_id, c.patient_name || '', null, { onDone: () => void reload() });
}

function onReassign() {
  const c = currentCase();
  if (!c) return;
  openAssignModal(c.case_id, c.patient_name || '', c.assigned_to || null, { onDone: () => void reload() });
}

/** Archive / close the open case (R-024 outcome capture). Reloads on success so
 *  the header flips to the archived (read-only) state. */
function onArchive() {
  const c = currentCase();
  if (!c) return;
  if (isClosedCase(c)) {
    toast('Case is already archived', 'err');
    return;
  }
  openArchiveModal(c.case_id, c.patient_name || '', c.reference_number || '', { onDone: () => void reload() });
}

/**
 * Register the workspace overlay's actions (call once at boot). The overlay
 * itself is created lazily on the first openWorkspace().
 * @returns {void}
 */
export function registerWorkspace() {
  wireOnce();
}
